/**
 * Worksheet list screen — shows worksheets in the current folder.
 */
import { getState, loadFolders, loadWorksheets, createWorksheet, deleteWorksheet, duplicateWorksheet, moveWorksheet, setCurrentFolder, loadWorksheet, setCurrentStep } from '../services/state';
import { folderService } from '../services/api';
import { sanitize, getStatusDef, STEPS, cap } from '../services/utils';
import { isPlatformAdmin } from '../services/auth';
import { renderFoldersScreen } from './folders';

let _container: HTMLElement;

export async function renderWorksheetListScreen(container: HTMLElement): Promise<void> {
  _container = container;
  // Ensure folders are loaded (needed after direct navigation via hash)
  if (getState().folders.length === 0) {
    await loadFolders();
  }
  await loadWorksheets();
  render();
}

function render(): void {
  const { worksheets, currentFolderId, folders } = getState();
  const folder = folders.find((f) => f._id === currentFolderId);
  if (!folder) {
    renderFoldersScreen(_container);
    return;
  }

  const active = worksheets.filter((w) => w.status !== 'archived');
  const archived = worksheets.filter((w) => w.status === 'archived');
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  _container.innerHTML = `
    <div style="max-width:900px;margin:0 auto;padding:24px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:24px">
        <div style="display:flex;align-items:center;gap:12px">
          <button id="back-to-folders" style="color:var(--colorTextLight);background:none;border:none;cursor:pointer;padding:0">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
          </button>
          <svg width="20" height="20" fill="none" stroke="var(--colorAccent)" viewBox="0 0 24 24" stroke-width="2" style="flex-shrink:0"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
          <div>
            <h2 id="folder-heading-name" style="font-size:18px;font-weight:600;color:var(--colorTextHeading);cursor:default" title="Double-click to rename">${sanitize(folder.name)}</h2>
            <p id="folder-heading-refid" style="font-size:11px;color:var(--colorTextMuted);cursor:default" title="Double-click to edit reference ID">${folder.ref_id ? sanitize(folder.ref_id) : '<span style="opacity:.4;font-style:italic">No reference ID</span>'}</p>
          </div>
        </div>
        <button id="add-worksheet-btn" style="padding:8px 16px;border-radius:8px;font-size:13px;font-weight:500;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">
          + Worksheet
        </button>
      </div>

      <div id="worksheet-cards" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:12px">
        ${active.length === 0 && archived.length === 0 ? renderEmptyState() : active.map((w) => renderWorksheetCard(w, isDark)).join('')}
      </div>

      <div style="display:flex;justify-content:flex-end;gap:4px;margin-top:12px">
        <button id="reports-btn" style="display:flex;align-items:center;gap:4px;font-size:11px;color:var(--colorTextLight);background:none;border:none;cursor:pointer;padding:4px 8px" title="View folder reports">
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
          Reports
        </button>
        <input type="file" id="import-file-input" accept=".json" style="display:none">
        <button id="import-btn" style="display:flex;align-items:center;gap:4px;font-size:11px;color:var(--colorTextLight);background:none;border:none;cursor:pointer;padding:4px 8px" title="Import worksheet from JSON">
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
          Import
        </button>
      </div>

      ${archived.length > 0 ? `
        <div style="margin-top:24px;padding-top:16px;border-top:1px solid var(--colorBorder)">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);opacity:.6;margin-bottom:8px">Archived (${archived.length})</div>
          <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:8px">
            ${archived.map((w) => renderArchivedCard(w)).join('')}
          </div>
        </div>
      ` : ''}
    </div>
  `;

  // Events
  document.getElementById('back-to-folders')?.addEventListener('click', () => {
    window.location.hash = 'folders';
  });
  // Folder name double-click to rename
  document.getElementById('folder-heading-name')?.addEventListener('dblclick', () => {
    const el = document.getElementById('folder-heading-name')!;
    const { currentFolderId, folders } = getState();
    const folder = folders.find((f) => f._id === currentFolderId);
    if (!folder) return;
    const current = folder.name;
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;align-items:center;gap:6px';
    const input = document.createElement('input');
    input.type = 'text';
    input.value = current;
    input.style.cssText = 'font-size:18px;font-weight:600;color:var(--colorTextHeading);padding:2px 8px;border-radius:6px;border:1px solid var(--colorBorderInput);background:var(--colorBgInput);width:240px';
    const btn = document.createElement('button');
    btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--colorSuccess)" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>';
    btn.style.cssText = 'cursor:pointer;background:none;border:none;padding:2px;line-height:0';
    wrapper.appendChild(input);
    wrapper.appendChild(btn);
    el.replaceWith(wrapper);
    input.focus();
    input.select();
    let committed = false;
    async function commit() {
      if (committed) return;
      committed = true;
      const val = input.value.trim() || current;
      try {
        const { folderService } = await import('../services/api');
        await folderService.update(currentFolderId!, { name: val });
        await loadFolders();
      } catch { /* ignore */ }
      render();
    }
    btn.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') { input.value = current; commit(); }
    });
    input.addEventListener('blur', () => setTimeout(() => { if (!committed) commit(); }, 150));
  });

  // Reference ID double-click to edit
  document.getElementById('folder-heading-refid')?.addEventListener('dblclick', () => {
    const el = document.getElementById('folder-heading-refid')!;
    const { currentFolderId, folders } = getState();
    const folder = folders.find((f) => f._id === currentFolderId);
    if (!folder) return;
    const current = folder.ref_id || '';
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;align-items:center;gap:6px';
    const input = document.createElement('input');
    input.type = 'text';
    input.value = current;
    input.placeholder = 'Reference ID';
    input.style.cssText = 'font-size:11px;color:var(--colorTextMuted);padding:2px 6px;border-radius:4px;border:1px solid var(--colorBorderInput);background:var(--colorBgInput);width:200px';
    const btn = document.createElement('button');
    btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--colorSuccess)" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>';
    btn.style.cssText = 'cursor:pointer;background:none;border:none;padding:2px;line-height:0';
    wrapper.appendChild(input);
    wrapper.appendChild(btn);
    el.replaceWith(wrapper);
    input.focus();
    input.select();
    let committed = false;
    async function commit() {
      if (committed) return;
      committed = true;
      const val = input.value.trim();
      try {
        const { folderService } = await import('../services/api');
        await folderService.update(currentFolderId!, { ref_id: val });
        await loadFolders();
      } catch { /* ignore */ }
      render();
    }
    btn.addEventListener('click', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') { input.value = current; commit(); }
    });
    input.addEventListener('blur', () => setTimeout(() => { if (!committed) commit(); }, 150));
  });

  document.getElementById('add-worksheet-btn')?.addEventListener('click', () => handleAddWorksheet());
  document.getElementById('empty-add')?.addEventListener('click', () => handleAddWorksheet());
  document.getElementById('empty-create')?.addEventListener('click', () => handleAddWorksheet());

  // Reports
  document.getElementById('reports-btn')?.addEventListener('click', () => {
    const { currentFolderId } = getState();
    if (currentFolderId) window.location.hash = `folder/${currentFolderId}/reports`;
  });

  // Import
  document.getElementById('import-btn')?.addEventListener('click', () => {
    document.getElementById('import-file-input')?.click();
  });
  document.getElementById('import-file-input')?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    (e.target as HTMLInputElement).value = '';
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.useCase) { alert('Invalid file: no use case data found.'); return; }
      const { worksheetService } = await import('../services/api');
      await worksheetService.import(currentFolderId!, data.useCase);
      await loadWorksheets();
      render();
    } catch (err: any) {
      if (err.statusCode === 409) {
        alert(err.message + ' Use a different file or rename the existing worksheet.');
      } else {
        alert('Import failed: ' + (err.message || 'Unknown error'));
      }
    }
  });

  // Open worksheet (click card body)
  document.querySelectorAll('[data-open-worksheet]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.openWorksheet!;
      window.location.href = `/worksheet?server=1&folderId=${currentFolderId}&worksheetId=${id}`;
    });
  });

  // Toggle step drawer — slides up inside the card using max-height
  document.querySelectorAll('[data-toggle-steps]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.toggleSteps!;
      const drawer = document.querySelector(`[data-steps-for="${id}"]`) as HTMLElement;
      const chevron = document.querySelector(`[data-chevron-for="${id}"]`) as HTMLElement;
      if (!drawer) return;
      const isOpen = drawer.style.maxHeight && drawer.style.maxHeight !== '0px';
      // Close all other drawers
      document.querySelectorAll('[data-steps-for]').forEach((d) => {
        (d as HTMLElement).style.maxHeight = '0';
        (d as HTMLElement).style.borderTopColor = 'transparent';
      });
      document.querySelectorAll('[data-chevron-for]').forEach((c) => {
        (c as HTMLElement).style.transform = '';
      });
      if (!isOpen) {
        drawer.style.maxHeight = '42px';
        drawer.style.borderTopColor = 'var(--colorBorder)';
        if (chevron) chevron.style.transform = 'rotate(180deg)';
      }
    });
  });

  bindDrawerEvents();
}

function bindDrawerEvents(): void {
  const { currentFolderId } = getState();

  // Step jump navigation
  document.querySelectorAll('[data-step-jump]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      const id = (el as HTMLElement).dataset.stepJump!;
      const step = (el as HTMLElement).dataset.step!;
      if (step === 'output') {
        window.location.href = `/worksheet?server=1&folderId=${currentFolderId}&worksheetId=${id}&screen=output`;
      } else {
        window.location.href = `/worksheet?server=1&folderId=${currentFolderId}&worksheetId=${id}&step=${step}`;
      }
    });
  });

  // Delete worksheet — confirm inline in the drawer
  document.querySelectorAll('[data-delete-worksheet]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.deleteWorksheet!;
      const drawer = document.querySelector(`[data-steps-for="${id}"]`) as HTMLElement;
      if (!drawer) return;
      const inner = drawer.querySelector('div')!;
      const originalHTML = inner.innerHTML;
      inner.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;gap:8px;padding:4px 0">
          <span style="font-size:11px;color:var(--colorError);font-weight:500">Delete permanently?</span>
          <button data-confirm-delete="${id}" style="padding:3px 12px;border-radius:6px;font-size:10px;font-weight:600;cursor:pointer;border:none;background:var(--colorError);color:#fff">Delete</button>
          <button data-cancel-delete style="padding:3px 12px;border-radius:6px;font-size:10px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgCard);color:var(--colorText)">Cancel</button>
        </div>
      `;
      inner.querySelector('[data-confirm-delete]')?.addEventListener('click', async (ev) => {
        (ev as Event).stopPropagation();
        await deleteWorksheet(id);
        render();
      });
      inner.querySelector('[data-cancel-delete]')?.addEventListener('click', (ev) => {
        (ev as Event).stopPropagation();
        inner.innerHTML = originalHTML;
        bindDrawerEvents();
      });
    });
  });

  // Duplicate worksheet
  document.querySelectorAll('[data-duplicate-worksheet]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.duplicateWorksheet!;
      await duplicateWorksheet(id);
      render();
    });
  });

  // Move worksheet
  document.querySelectorAll('[data-move-worksheet]').forEach((el) => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.moveWorksheet!;
      await showMoveModal(id);
    });
  });
}

function renderWorksheetCard(w: any, isDark: boolean): string {
  const s = getStatusDef(w.status);
  const { currentFolderId } = getState();
  const st = w.stats || {};
  const tplAbbr = w.template_abbr || '';
  const tplLabel = w.template_label || '';
  const stage = w.environment?.stage ? cap(w.environment.stage) : '';

  return `
    <div style="background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:var(--radiusLg);cursor:pointer;transition:border-color .15s;box-shadow:var(--shadowCard);overflow:hidden;display:flex;flex-direction:column;position:relative" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'">
      <div data-open-worksheet="${w._id}" style="padding:14px 16px;flex:1">
        <div style="display:flex;align-items:start;justify-content:space-between;margin-bottom:6px">
          <div style="display:flex;align-items:center;gap:8px">
            ${tplAbbr ? `<span style="width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:6px;font-size:9px;font-weight:700;background:var(--colorBgElevated);border:1px solid var(--colorBorder);flex-shrink:0;color:var(--colorTextHeading)">${sanitize(tplAbbr)}</span>` : ''}
            <div>
              <div style="font-size:13px;font-weight:500;color:var(--colorTextHeading)">${sanitize(w.name) || 'Untitled Worksheet'}</div>
              <div style="font-size:10px;color:var(--colorTextMuted)">${tplLabel ? sanitize(tplLabel) : 'Custom'}${stage ? ' · ' + stage : ''}</div>
            </div>
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:2px;flex-shrink:0">
            ${w.updated_at ? `<span style="font-size:9px;color:var(--colorTextLight)">${new Date(w.updated_at).toLocaleDateString(undefined, {month:'short', day:'numeric', year:'numeric'})}</span>` : ''}
          </div>
        </div>
        ${w.description ? `<div style="font-size:11px;color:var(--colorTextLight);margin-bottom:6px;line-height:1.4">${sanitize(w.description).substring(0, 120)}</div>` : ''}
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:10px;margin-top:4px">
          <span style="font-weight:500;color:var(--colorTextHeading)">${st.actor_count || 0} Actors</span>
          ${st.missing ? `<span style="color:var(--colorError)">${st.missing} Missing</span>` : ''}
          ${st.planned ? `<span style="color:var(--colorAccent)">${st.planned} Planned</span>` : ''}
          ${st.func_gaps ? `<span style="color:var(--colorError)">${st.func_gaps} Gaps</span>` : ''}
          ${st.callouts ? `<span style="color:var(--colorWarning)">${st.callouts} Callouts</span>` : ''}
          ${st.tickets ? `<span style="color:var(--colorTextMuted)">${st.tickets} Tickets</span>` : ''}
          <span style="margin-left:auto;font-size:9px;padding:2px 8px;border-radius:9999px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:${s.color};background:${isDark ? s.darkBg : s.bg}">${s.label}</span>
        </div>
      </div>
      <!-- Chevron toggle at bottom -->
      <div style="display:flex;justify-content:center;padding:2px 0">
        <button data-toggle-steps="${w._id}" style="background:none;border:none;cursor:pointer;padding:0 16px;color:var(--colorTextLight);opacity:.3;transition:opacity .15s;line-height:0" onmouseover="this.style.opacity='.8'" onmouseout="this.style.opacity='.3'" title="Actions & navigation">
          <svg data-chevron-for="${w._id}" width="12" height="8" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" style="transition:transform .25s ease"><path stroke-linecap="round" stroke-linejoin="round" d="M18 15l-6-6-6 6"/></svg>
        </button>
      </div>
      <!-- Slide-up drawer (overlays card content from the bottom) -->
      <div data-steps-for="${w._id}" style="position:absolute;bottom:0;left:0;right:0;max-height:0;overflow:hidden;transition:max-height .25s ease;background:var(--colorBgElevated);border-top:1px solid transparent;border-radius:0 0 var(--radiusLg) var(--radiusLg);z-index:2">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 12px">
          <div style="display:flex;align-items:center;gap:3px">
            ${STEPS.map((name, i) =>
              `<a data-step-jump="${w._id}" data-step="${i}" class="step-pill-hover" style="display:inline-flex;align-items:center;justify-content:center;min-width:24px;height:24px;border-radius:12px;font-size:10px;font-weight:600;background:var(--colorPrimary);opacity:.5;color:#fff;cursor:pointer;text-decoration:none;overflow:hidden;white-space:nowrap;transition:all .2s ease;padding:0 6px" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='.5'" title="${name}"><span class="sp-num">${i + 1}</span><span class="sp-label" style="max-width:0;overflow:hidden;opacity:0;margin-left:0;transition:max-width .2s ease,opacity .15s ease,margin-left .2s ease;font-weight:500">${name}</span></a>`
            ).join('')}
            <a data-step-jump="${w._id}" data-step="output" class="step-pill-hover" style="display:inline-flex;align-items:center;justify-content:center;min-width:24px;height:24px;border-radius:12px;font-size:10px;font-weight:600;background:var(--colorSuccess);color:#fff;cursor:pointer;text-decoration:none;overflow:hidden;white-space:nowrap;transition:all .2s ease;padding:0 6px" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='.7'" title="Results"><span class="sp-num" style="display:inline">R</span><span class="sp-label" style="max-width:0;overflow:hidden;opacity:0;margin-left:0;transition:max-width .2s ease,opacity .15s ease,margin-left .2s ease;font-weight:500">esults</span></a>
          </div>
          <div style="display:flex;align-items:center;gap:6px">
            <button data-move-worksheet="${w._id}" style="background:none;border:none;cursor:pointer;color:var(--colorTextLight);padding:2px;line-height:0;opacity:.5;transition:opacity .15s" title="Move" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='.5'">
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
            </button>
            <button data-duplicate-worksheet="${w._id}" style="background:none;border:none;cursor:pointer;color:var(--colorTextLight);padding:2px;line-height:0;opacity:.5;transition:opacity .15s" title="Copy" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='.5'">
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
            </button>
            <button data-delete-worksheet="${w._id}" style="background:none;border:none;cursor:pointer;color:var(--colorError);padding:2px;line-height:0;opacity:.4;transition:opacity .15s" title="Delete" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='.4'">
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderArchivedCard(w: any): string {
  return `
    <div data-open-worksheet="${w._id}" style="background:var(--colorBgElevated);border:1px solid var(--colorBorder);border-radius:8px;padding:10px;cursor:pointer;opacity:.5;transition:opacity .15s" onmouseover="this.style.opacity='.7'" onmouseout="this.style.opacity='.5'">
      <div style="font-size:11px;font-weight:500;color:var(--colorTextMuted)">${sanitize(w.name) || 'Untitled'}</div>
      <div style="font-size:9px;color:var(--colorTextLight);margin-top:2px">${w.updated_at ? new Date(w.updated_at).toLocaleDateString() : ''}</div>
    </div>
  `;
}

function renderEmptyState(): string {
  return `
    <div style="grid-column:1/-1;text-align:center;padding:60px 0">
      <div id="empty-add" style="width:64px;height:64px;border-radius:50%;border:2px dashed var(--colorBorder);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;cursor:pointer">
        <svg width="24" height="24" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m6-6H6"/></svg>
      </div>
      <p style="font-size:13px;color:var(--colorTextLight);margin-bottom:12px">No worksheets yet.</p>
      <button id="empty-create" style="padding:8px 16px;border-radius:8px;font-size:12px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorAccent)">Click to Add New</button>
    </div>
  `;
}

async function showMoveModal(worksheetId: string): Promise<void> {
  const { folders, currentFolderId } = getState();
  const otherFolders = folders.filter((f) => f._id !== currentFolderId);
  if (otherFolders.length === 0) {
    alert('No other folders to move to. Create a new folder first.');
    return;
  }
  let selectedId: string | null = null;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'modal-box';
  modal.innerHTML = `
    <h3 style="font-size:14px;font-weight:600;color:var(--colorTextHeading);margin-bottom:16px">Move Worksheet</h3>
    <div id="move-folder-list" style="display:flex;flex-direction:column;gap:6px;margin-bottom:16px;max-height:240px;overflow-y:auto">
      ${otherFolders.map((f) => `
        <div data-move-target="${f._id}" style="display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:8px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);transition:all .15s">
          <svg width="16" height="16" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
          <span style="flex:1;font-size:12px;color:var(--colorText)">${sanitize(f.name)}</span>
          <span class="move-check" style="width:16px;height:16px;border-radius:50%;border:2px solid var(--colorBorder);display:flex;align-items:center;justify-content:center;flex-shrink:0"></span>
        </div>
      `).join('')}
    </div>
    <div style="display:flex;gap:8px">
      <button type="button" id="move-cancel" style="flex:1;padding:10px;border-radius:8px;font-size:12px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
      <button type="button" id="move-confirm" style="flex:1;padding:10px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">Move</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  modal.querySelector('#move-cancel')?.addEventListener('click', () => overlay.remove());

  modal.querySelectorAll('[data-move-target]').forEach((el) => {
    el.addEventListener('click', () => {
      selectedId = (el as HTMLElement).dataset.moveTarget!;
      modal.querySelectorAll('.move-check').forEach((c) => { (c as HTMLElement).innerHTML = ''; (c as HTMLElement).style.borderColor = 'var(--colorBorder)'; });
      modal.querySelectorAll('[data-move-target]').forEach((r) => { (r as HTMLElement).style.borderColor = 'var(--colorBorder)'; });
      (el as HTMLElement).style.borderColor = 'var(--colorAccent)';
      const check = el.querySelector('.move-check') as HTMLElement;
      if (check) {
        check.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--colorSuccess)" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>';
        check.style.borderColor = 'var(--colorSuccess)';
      }
    });
  });

  modal.querySelector('#move-confirm')?.addEventListener('click', async () => {
    if (!selectedId) return;
    overlay.remove();
    await moveWorksheet(worksheetId, selectedId);
    render();
  });
}

async function handleAddWorksheet(): Promise<void> {
  const { currentFolderId } = getState();
  if (!currentFolderId) return;
  const worksheet = await createWorksheet('New Worksheet');
  if (worksheet) {
    window.location.href = `/worksheet?server=1&folderId=${currentFolderId}&worksheetId=${worksheet._id}`;
  }
}
